import { useState, useEffect, useRef } from 'react'
import { IOfferSpendFilters, IFilterConfig, EFilterFetchConvertType } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { useSearchParams } from 'react-router'
import { HISTORY_IS_EMPTY_OPTIONS } from '../constants'
import { ALL_LANG_KEY } from '../types/lang'
import { useTranslation } from 'react-i18next'

type IUseFiltersOfferSpendPage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	spendFiltersConfig: IFilterConfig<keyof IOfferSpendFilters>[];
	defaultFiltersValue: IOfferSpendFilters;
	initialFiltersValue: IOfferSpendFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IOfferSpendFilters | null)) => (IOfferSpendFilters | null)) | IOfferSpendFilters | null)) => void;
}

const OFFER_SPEND_FILTERS_DEFAULT: IOfferSpendFilters = {
	accountId: '',
	'account.auth.login': '',
	'account.extra.keitaroLogin': '',
	'role.inGroup': '',
	offerId: '',
	'offer.name': '',
	scopeId: '',
	'scope.name': '',
	at: ['', ''],
	'amount': ['', ''],
	'history.empty': null
}


export const useOfferSpendFiltersPage = (buyer: number): IUseFiltersOfferSpendPage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)
	const [buyerPrev, setBuyerPrev] = useState<number>(buyer)

	const profile = useAtomValue(state.profileAtom)
	const isBuyer = useAtomValue(state.isBuyerAtom)

	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IOfferSpendFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const HISTORY_IS_EMPTY_OPTIONS_TRANS = HISTORY_IS_EMPTY_OPTIONS(t)

	const spendFiltersConfig: IFilterConfig<keyof IOfferSpendFilters>[] = [
		{
			name: 'accountId',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_account_id),
			type: EFilterType.input,
			attr: { type: 'number' },
			operator: EFilterOperator.isIntLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.auth.login',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_account),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'account.extra.keitaroLogin',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_kt_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.inGroup',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_account_in_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'offerId',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_offer_id),
			type: EFilterType.input,
			attr: { type: 'number' },
			operator: EFilterOperator.isIntLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'offer.name',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_offer),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'scopeId',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_scope_id),
			type: EFilterType.input,
			attr: { type: 'number' },
			operator: EFilterOperator.isIntLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'scope.name',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_scope),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'at',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_date),
			type: EFilterType.dateRange,
			operator: EFilterOperator.isBetween,
			styles: { width: '280px' }
		},
		{
			name: 'amount',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_amount),
			type: EFilterType.numberRange,
			attr: { type: 'number' },
			operator: EFilterOperator.isBetween,
			styles: { width: '350px' },
			placeholder: t(ALL_LANG_KEY.offer_spend_crud_modal.field_amount_placeholder),
		},
		{
			name: 'history.empty',
			label: t(ALL_LANG_KEY.offer_spend_list_table.column_history),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: HISTORY_IS_EMPTY_OPTIONS_TRANS,
			styles: { minWidth: '200px' }
		}
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		spendFiltersConfig,
		OFFER_SPEND_FILTERS_DEFAULT
	)

	useEffect(() => {
		let _userFilters = userFilters
		//console.log('hook.useOfferSpendFiltersPage useEffect::START:: ', { send, buyer, buyerPrev, userFilters, _userFilters, filter })
		if (send && buyerPrev !== buyer) {
			// reset filters [RFC] onBuyer (menu tabs) changed
			setBuyerPrev(buyer)
			//console.log('!!! clear main filter:: send && buyerPrev !== buyer !!!')
			setFilter([]) // clear main filter with mixins
			_userFilters = OFFER_SPEND_FILTERS_DEFAULT
			setUserFilters(OFFER_SPEND_FILTERS_DEFAULT)
			setClearChildFilters(prev => prev + 1) // clearAllChildFilters command-flag
		}

		if (!!_userFilters) {
			setSend(true)
			//console.log('hook.useOfferSpendFiltersPage useEffect::MIDDLE:: ', { userFilters, _userFilters, filter })
			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				_userFilters,
				spendFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				if (!!isBuyer && `${isBuyer}` !== prev.get('buyer')) {
					prev.set('buyer', encodeURIComponent(buyer))
				}

				return prev
			})

			if (buyer) {
				newFilter.push([`accountId${EFilterOperator.isEqual}`, buyer])
			}

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			//console.log('hook.useOfferSpendFiltersPage:: useEffect::END:: ', { userFilters, _userFilters, filter, newFilter, newFilterStr })
		}
	}, [userFilters, buyer])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		spendFiltersConfig,
		defaultFiltersValue: OFFER_SPEND_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}